import { useResponsive } from './useResponsive';

/**
 * Horizontal breathing room (px) kept on each side of a modal on small devices,
 * so it never touches the screen edge. 16px × 2 = 32px total.
 */
const HORIZONTAL_MARGIN = 32;

/**
 * Smallest width we ever allow a modal to shrink to. Below this the content
 * becomes unusable; better to let it scroll horizontally inside the modal.
 */
const MIN_MODAL_WIDTH = 280;

/**
 * Clamp a desired modal width to what the current viewport can actually show.
 *
 * - Numeric pixel widths (e.g. 1400, 550) are capped to `viewportWidth - margin`
 *   so they never overflow a phone (<430px) or iPad portrait (≤1200px).
 * - String widths (e.g. '90vw', '75%') are returned untouched — the browser
 *   already sizes those relative to the viewport.
 *
 * Pure function so it can be unit-tested and reused outside React.
 */
export const clampModalWidth = (
  desired: number | string,
  viewportWidth: number,
): number | string => {
  if (typeof desired !== 'number') return desired;
  if (viewportWidth <= 0) return desired;
  const cap = Math.max(MIN_MODAL_WIDTH, viewportWidth - HORIZONTAL_MARGIN);
  return Math.min(desired, cap);
};

/**
 * Returns a Safari/iPad/phone-safe modal width.
 *
 * Width comes from `useResponsive()` which is driven by `window.visualViewport`,
 * so the cap stays correct even while Safari's iOS toolbar collapses/expands
 * (the classic Safari-vs-Chrome viewport discrepancy).
 *
 * Usage:
 *   const width = useModalWidth(1400);
 *   <Modal width={width} ... />
 */
export const useModalWidth = (desktopWidth: number | string): number | string => {
  const { width } = useResponsive();
  return clampModalWidth(desktopWidth, width);
};
